import {AxiosInstance} from "axios";
import {
    BirthdayGift,
    BirthdayGiftRequest,
    BirthdayGiftSettings,
    CustomerBirthdayVoucher,
    GiftBatchRecipient,
    GiftBatchResult,
    GiftStatistics,
    GiftVoucherScan,
    TodayBirthdayVoucher,
    VoucherCheckResult
} from "@/birthday-gift/interfaces";
import {PaginatedData, PaginationRequest} from "@/core/interfaces/api";

/**
 * Resource for the HDS birthday gift endpoints (module « Cadeaux d'anniversaire »).
 */
const BirthdayGiftResource = (hdsClient: AxiosInstance) => {
    return {

        /**
         * Get the birthday gifts of the organization.
         */
        async index(): Promise<BirthdayGift[]> {
            const response = await hdsClient.get('/birthday-gifts');
            return response.data.data;
        },

        /**
         * Create a birthday gift. Rejects with a 422 carrying
         * `conflicting_gifts` when two auto gifts target overlapping profiles.
         */
        async create(data: BirthdayGiftRequest): Promise<BirthdayGift> {
            const response = await hdsClient.post('/birthday-gifts', {data});
            return response.data.data;
        },

        /**
         * Get a birthday gift.
         */
        async read(giftId: number): Promise<BirthdayGift> {
            const response = await hdsClient.get(`/birthday-gifts/${giftId}`);
            return response.data.data;
        },

        /**
         * Update a birthday gift. Pausing (`active`) and toggling the
         * automatic send (`auto_send`) go through this partial update.
         */
        async update(giftId: number, data: Partial<BirthdayGiftRequest>): Promise<BirthdayGift> {
            const response = await hdsClient.patch(`/birthday-gifts/${giftId}`, {data});
            return response.data.data;
        },

        /**
         * Soft delete a birthday gift (its statistics are kept).
         */
        async remove(giftId: number): Promise<void> {
            await hdsClient.delete(`/birthday-gifts/${giftId}`);
        },

        /**
         * Get the organization settings (video + send hour),
         * auto-created with defaults on first read.
         */
        async settings(): Promise<BirthdayGiftSettings> {
            const response = await hdsClient.get('/birthday-gift-settings');
            return response.data.data;
        },

        /**
         * Update the organization settings. For a stock video the server
         * resolves and snapshots the video URL itself.
         */
        async updateSettings(data: BirthdayGiftSettings): Promise<BirthdayGiftSettings> {
            const response = await hdsClient.put('/birthday-gift-settings', {data});
            return response.data.data;
        },

        /**
         * Get the vouchers of today's birthdays.
         */
        async today(): Promise<TodayBirthdayVoucher[]> {
            const response = await hdsClient.get('/birthday-vouchers/today');
            return response.data.data;
        },

        /**
         * Override the gift of a voucher, or exclude/include the patient,
         * while the message has not been sent yet.
         */
        async updateVoucher(
            voucherId: number,
            data: {birthday_gift_id?: number, excluded?: boolean}
        ): Promise<TodayBirthdayVoucher> {
            const response = await hdsClient.patch(`/birthday-vouchers/${voucherId}`, {data});
            return response.data.data;
        },

        /**
         * Manually send the message of a voucher pending validation.
         */
        async sendVoucher(voucherId: number): Promise<TodayBirthdayVoucher> {
            const response = await hdsClient.post(`/birthday-vouchers/${voucherId}/send`);
            return response.data.data;
        },

        /**
         * Send the birthday gift right away to a chosen patient, outside the
         * daily eligibility (any patient, any day).
         */
        async sendToCustomer(customerId: number, giftId?: number): Promise<TodayBirthdayVoucher> {
            const response = await hdsClient.post(
                `/customers/${customerId}/birthday-gift-voucher/send`,
                {data: giftId ? {birthday_gift_id: giftId} : {}}
            );
            return response.data.data;
        },

        /**
         * Send the birthday messages of a whole day at once, the agenda's
         * « send to everyone ». Each recipient may carry the gift the
         * pharmacist picked; without one the server sends plain wishes.
         *
         * Refusals are reported per patient rather than thrown: one
         * unreachable patient must not hide what left for the others.
         */
        async sendBatch(organizationId: number, recipients: GiftBatchRecipient[]): Promise<GiftBatchResult> {
            const response = await hdsClient.post(
                `/organizations/${organizationId}/birthday-gifts/send`,
                {data: {recipients}}
            );

            return {
                outcomes: response.data.data,
                sent_count: response.data.sent_count
            };
        },

        /**
         * Get the statistics of a gift.
         */
        async statistics(giftId: number): Promise<GiftStatistics> {
            const response = await hdsClient.get(`/birthday-gifts/${giftId}/statistics`);
            return response.data.data;
        },

        /**
         * Get the paginated voucher/scan list of a gift.
         */
        async scans(giftId: number, params: PaginationRequest = {}): Promise<PaginatedData<GiftVoucherScan[]>> {
            const response = await hdsClient.get(`/birthday-gifts/${giftId}/vouchers`, {params});
            return response.data;
        },

        /**
         * Check a counter code (scanned or typed). POST so codes never
         * land in access logs.
         */
        async checkCode(code: string): Promise<VoucherCheckResult> {
            const response = await hdsClient.post('/birthday-vouchers/check', {data: {code}});
            return response.data.data;
        },

        /**
         * Mark the gift of a code as delivered (single use).
         *
         * Answers the same shape as `checkCode`: 409/410/404 carry the reason
         * why the code could not be honoured.
         */
        async redeem(code: string): Promise<VoucherCheckResult> {
            const response = await hdsClient.post('/birthday-vouchers/redeem', {data: {code}});
            return response.data.data;
        },

        /**
         * Get the current-year voucher of a customer (patient file insert).
         */
        async customerVoucher(customerId: number): Promise<CustomerBirthdayVoucher | null> {
            const response = await hdsClient.get(`/customers/${customerId}/birthday-gift-voucher`);
            return response.data?.data ?? null;
        },
    };
};

export default BirthdayGiftResource;
